"use client";

import { useEffect, useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

const ROLE_PAGE_ACCESS = {
    property_team: [
        'sold',
        'rejected',
        'approved',
        'pending',
        'queries',
        'categories',
    ],
    services_team: [
        'service_booking',
        'service_agent_management',
        'category_management',
    ],
    loan_team: [
        'loan_management',
        'queries',
    ],
    support_team: [
        'queries',
    ],
};

export default function CreateAdmin() {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [allowedPages, setAllowedPages] = useState([]);

    const currentAdmin = JSON.parse(sessionStorage.getItem("user")) || {};
    const isSuperAdmin = currentAdmin?.role === "admin";
    const isTeamLeader = currentAdmin?.leader === true;
    const currentRole = currentAdmin?.role;


    const [form, setForm] = useState({
        email: "",
        phone: "",
        image: "",
        userName: "",
        password: "",
        role: "",
        access: {
            page: [],
        },
        teamLeader: false,
    });

    const ROLE_OPTIONS = isSuperAdmin
        ? ["property_team", "services_team", "loan_team", "support_team"]
        : isTeamLeader
            ? [currentRole]
            : [];


    const togglePageAccess = (page) => {
        setForm((prev) => ({
            ...prev,
            access: {
                ...prev.access,
                page: prev.access.page.includes(page)
                    ? prev.access.page.filter((p) => p !== page)
                    : [...prev.access.page, page],
            },
        }));
    };

    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            access: { page: [] },
        }));
    }, [form.role]);

    useEffect(() => {
        if (isTeamLeader && currentRole) {
            setForm((prev) => ({
                ...prev,
                role: currentRole,
            }));
        }
    }, [isTeamLeader, currentRole]);

    useEffect(() => {
        if (form.role && ROLE_PAGE_ACCESS[form.role]) {
            setAllowedPages(ROLE_PAGE_ACCESS[form.role]);
        } else {
            setAllowedPages([]);
        }
    }, [form.role]);

    const validateForm = (form) => {
        const errors = {};

        // Username
        if (!form.userName) {
            errors.userName = "Username is required";
        } else if (/\s/.test(form.userName)) {
            errors.userName = "Username must not contain spaces";
        } else if (form.userName.length < 4) {
            errors.userName = "Username must be at least 4 characters";
        }

        // Email (optional)
        if (
            form.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
        ) {
            errors.email = "Invalid email address";
        }

        // Phone
        if (!form.phone) {
            errors.phone = "Phone number is required";
        } else if (!/^[0-9]{10}$/.test(form.phone)) {
            errors.phone = "Phone must be 10 digits";
        }

        // Password
        if (!form.password) {
            errors.password = "Password is required";
        } else if (form.password.length < 6) {
            errors.password = "Password must be at least 6 characters";
        }

        // Role
        if (!form.role) {
            errors.role = "Role is required";
        }

        if (!isTeamLeader && !form.teamLeader && form.access.page.length === 0) {
            errors.access = "Select at least one access page";
        }

        return errors;
    };

    console.log("data:::: ,", allowedPages)

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm(form);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            showErrorToast("Please fix the highlighted errors");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                email: form.email,
                phone: form.phone,
                image: form.image,
                role: form.role,
                teamLeader: form.teamLeader,
                access: {
                    page: form.teamLeader ? [] : form.access.page,
                },
                userName: form.userName,
                password: form.password,
            };


            const res = await makeRequestAuth(
                "post",
                "/admin/SaveNewAdmin",
                payload
            );

            if (res?.error) {
                showErrorToast(res.message);
                return;
            }

            showSuccessToast("Admin created successfully");
            setTimeout(() => window.location.reload(), 1500)
            setErrors({});
        } catch (err) {
            showErrorToast(err.message || "Failed to create admin");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <ToastContainer />

            <h2 className="text-2xl font-bold mb-6">Create New Admin</h2>

            <form
                onSubmit={handleSubmit}
                className="bg-white shadow rounded-lg p-6 space-y-5"
            >
                {/* Username */}
                <div>
                    <input
                        type="text"
                        placeholder="Username (no spaces)"
                        className={`w-full border p-2 rounded ${errors.userName ? "border-red-500" : ""
                            }`}
                        value={form.userName}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                userName: e.target.value.replace(/\s/g, ""), // ❌ no spaces
                            })
                        }
                    />
                    {errors.userName && (
                        <p className="text-red-500 text-sm mt-1">{errors.userName}</p>
                    )}
                </div>

                {/* Email */}
                <div>
                    <input
                        type="email"
                        placeholder="Email (optional)"
                        className={`w-full border p-2 rounded ${errors.email ? "border-red-500" : ""
                            }`}
                        value={form.email}
                        onChange={(e) =>
                            setForm({ ...form, email: e.target.value })
                        }
                    />
                    {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                    )}
                </div>

                {/* Phone */}
                <div>
                    <input
                        type="text"
                        placeholder="Phone"
                        className={`w-full border p-2 rounded ${errors.phone ? "border-red-500" : ""
                            }`}
                        value={form.phone}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                phone: e.target.value.replace(/\D/g, ""), // numbers only
                            })
                        }
                    />
                    {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                    )}
                </div>

                {/* Image */}
                <div>
                    <input
                        type="text"
                        placeholder="Profile Image URL (optional)"
                        className="w-full border p-2 rounded"
                        value={form.image}
                        onChange={(e) =>
                            setForm({ ...form, image: e.target.value })
                        }
                    />
                </div>

                {/* Password */}
                <div>
                    <input
                        type="password"
                        placeholder="Password"
                        className={`w-full border p-2 rounded ${errors.password ? "border-red-500" : ""
                            }`}
                        value={form.password}
                        onChange={(e) =>
                            setForm({ ...form, password: e.target.value })
                        }
                    />
                    {errors.password && (
                        <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                    )}
                </div>

                {/* ROLE (Single Select) */}
                <div>
                    <p className="font-semibold mb-2">Role</p>
                    <div className="flex flex-wrap gap-3">
                        {ROLE_OPTIONS.map((role) => (
                            <button
                                type="button"
                                key={role}
                                disabled={isTeamLeader}
                                onClick={() =>
                                    setForm((prev) => ({ ...prev, role }))
                                }
                                className={`px-4 py-2 rounded border font-medium
    ${form.role === role ? "bg-[#24ADD7] text-white" : "bg-gray-100"}
    ${isTeamLeader ? "opacity-60 cursor-not-allowed" : ""}
  `}
                            >
                                {role}
                            </button>

                        ))}
                    </div>
                    {errors.role && (
                        <p className="text-red-500 text-sm mt-1">{errors.role}</p>
                    )}
                </div>

                {isSuperAdmin && (
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={form.teamLeader}
                            onChange={(e) =>
                                setForm({ ...form, teamLeader: e.target.checked })
                            }
                        />
                        <label className="font-medium">Team Leader</label>
                    </div>
                )}


                {/* ACCESS PAGE (Multi Select) */}
                {/* ACCESS PAGE (Multi Select) */}
                {/* ACCESS PAGES */}
                <div className="flex flex-wrap gap-3">
                    {allowedPages.map((page) => {
                        const isActive = form.access.page.includes(page);

                        return (
                            <button
                                type="button"
                                key={page}
                                disabled={form.teamLeader}
                                onClick={() => togglePageAccess(page)}
                                className={`px-4 py-2 rounded border
          ${isActive ? "bg-green-600 text-white" : "bg-gray-100"}
          ${form.teamLeader ? "opacity-50 cursor-not-allowed" : ""}
        `}
                            >
                                {page}
                            </button>
                        );
                    })}
                </div>


                {/* SUBMIT */}
                <button
                    disabled={loading}
                    className="w-full bg-[#24ADD7] text-white py-3 rounded font-semibold hover:bg-[#106D87] disabled:opacity-50"
                >
                    {loading ? "Creating..." : "Create Admin"}
                </button>
            </form>
        </div>
    );

}
